/**
 * Reading a bank statement someone has pasted in.
 *
 * WHY PASTING RATHER THAN A FILE UPLOAD
 * -------------------------------------
 * Pakistani banks hand out statements as a CSV download or an emailed sheet,
 * and the person doing the reconciling already has it open. Selecting the
 * rows and pasting is fewer steps than saving a file and finding it again,
 * and it works the same whether the source is a download, an email, or a
 * portal that only lets you look at the table.
 *
 * WHAT IT ACCEPTS
 * ---------------
 * No two banks agree on a format, so this reads the shapes they actually
 * produce rather than insisting on one:
 *
 *   date, description, amount            a single signed column
 *   date, description, debit, credit     two columns, one of them empty
 *
 * A header row is used when it is there and skipped when it is not. Tabs work
 * as well as commas, because pasting from a spreadsheet gives tabs.
 *
 * WHAT IT REFUSES
 * ---------------
 * Anything it cannot read is reported with its line number rather than
 * dropped. A statement quietly short by three rows still reconciles to
 * something, and that something is wrong.
 */

/** Header words a bank might use, mapped to what this needs. */
const HEADINGS = {
  date: ['date', 'txn date', 'transaction date', 'value date', 'posting date'],
  description: ['description', 'particulars', 'narration', 'details', 'transaction details'],
  amount: ['amount', 'signed amount'],
  debit: ['debit', 'withdrawal', 'withdrawals', 'dr', 'paid out'],
  credit: ['credit', 'deposit', 'deposits', 'cr', 'paid in'],
  reference: ['reference', 'ref', 'cheque', 'cheque no', 'instrument'],
};

const normalise = (text) =>
  String(text ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

/**
 * Split one row into cells, respecting quotes.
 *
 * A naive split on the separator is wrong for real bank files, and wrong in
 * the way that matters: an amount written "1,234.56" becomes the cell "1",
 * and a statement imports as a hundred and twenty rupees instead of a
 * hundred and twenty thousand. Nothing about the result looks damaged.
 *
 * A doubled quote inside a quoted cell is an escaped quote, which is what
 * every spreadsheet emits.
 */
function splitRow(row) {
  // A tab wins if present: pasting from a spreadsheet gives tabs, and a
  // description may legitimately contain a comma.
  const separator = row.includes('\t') ? '\t' : ',';

  const cells = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < row.length; i++) {
    const char = row[i];

    if (char === '"') {
      if (quoted && row[i + 1] === '"') {
        cell += '"';
        i++;
      } else {
        quoted = !quoted;
      }
      continue;
    }

    if (char === separator && !quoted) {
      cells.push(cell.trim());
      cell = '';
      continue;
    }

    cell += char;
  }

  cells.push(cell.trim());
  return cells;
}

/** Which column holds what, if the first row names them. */
function readHeader(cells) {
  const found = {};

  cells.forEach((cell, index) => {
    const name = normalise(cell);
    for (const [field, aliases] of Object.entries(HEADINGS)) {
      if (aliases.includes(name)) found[field] = index;
    }
  });

  // A header has to name at least a date and something to describe it, or it
  // is just a row that happens to start with text.
  const usable = found.date !== undefined && found.description !== undefined;
  return usable ? found : null;
}

/**
 * A number as banks write it: 1,234.56 — and (1,234.56) or a trailing DR for
 * money going out, which several local banks use instead of a minus sign.
 */
function readAmount(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return null;

  const bracketed = /^\(.*\)$/.test(raw);
  const markedDebit = /\bdr\.?$/i.test(raw);

  const digits = raw
    .replace(/[()]/g, '')
    .replace(/\b[dc]r\.?$/i, '')
    .replace(/[,\s]/g, '')
    .replace(/^(rs\.?|pkr)/i, '');

  if (!/^-?\d*\.?\d+$/.test(digits)) return null;

  const value = Number.parseFloat(digits);
  if (!Number.isFinite(value)) return null;

  return bracketed || markedDebit ? -Math.abs(value) : value;
}

/** A date as banks write it, returned as YYYY-MM-DD. */
function readDate(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return null;

  // Already the way it will be stored.
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  /**
   * Day first, which is how dates are written in Pakistan. There is no way to
   * tell 03/04/2026 from 04/03/2026 by looking, so this commits to the local
   * convention rather than guessing per row — a rule that is sometimes wrong
   * is recoverable; a rule that changes halfway down the page is not.
   */
  const parts = raw.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})/);
  if (parts) {
    const [, day, month, year] = parts;
    const fullYear = year.length === 2 ? `20${year}` : year;
    const asDate = `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    return isRealDate(asDate) ? asDate : null;
  }

  // "25 Aug 2026" and "25-Aug-26".
  const named = raw.match(/^(\d{1,2})[\s\-/]([A-Za-z]{3,})[\s\-/](\d{2,4})/);
  if (named) {
    const [, day, monthName, year] = named;
    const month = MONTHS.indexOf(monthName.slice(0, 3).toLowerCase());
    if (month === -1) return null;

    const fullYear = year.length === 2 ? `20${year}` : year;
    return `${fullYear}-${String(month + 1).padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return null;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * A date that exists.
 *
 * 31/02 parses happily into "2026-02-31" and would be stored as a day that
 * never happened, sorting between the 30th and the 1st for ever.
 */
function isRealDate(text) {
  const [year, month, day] = text.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/**
 * Turn pasted text into lines ready to send, and say what could not be read.
 *
 * @returns {{lines: object[], problems: string[], skipped: number}}
 */
export function parseStatement(text) {
  const rows = String(text ?? '')
    .split(/\r?\n/)
    .map((row) => row.trim())
    .filter(Boolean);

  if (rows.length === 0) return { lines: [], problems: [], skipped: 0 };

  const header = readHeader(splitRow(rows[0]));
  const columns = header ?? { date: 0, description: 1, amount: 2 };
  const body = header ? rows.slice(1) : rows;

  const lines = [];
  const problems = [];

  body.forEach((row, index) => {
    // Numbered as the person sees them, counting the header if there was one.
    const lineNumber = index + 1 + (header ? 1 : 0);
    const cells = splitRow(row);

    const txnDate = readDate(cells[columns.date]);
    const description = String(cells[columns.description] ?? '').trim();

    let amount = null;
    if (columns.amount !== undefined) {
      amount = readAmount(cells[columns.amount]);
    }
    if (amount === null && (columns.debit !== undefined || columns.credit !== undefined)) {
      const debit = readAmount(cells[columns.debit]);
      const credit = readAmount(cells[columns.credit]);

      // Money out is negative, whichever column the bank put it in.
      if (debit !== null && debit !== 0) amount = -Math.abs(debit);
      else if (credit !== null && credit !== 0) amount = Math.abs(credit);
    }

    if (!txnDate) {
      problems.push(`Line ${lineNumber}: could not read the date`);
      return;
    }
    if (!description) {
      problems.push(`Line ${lineNumber}: no description`);
      return;
    }
    if (amount === null || amount === 0) {
      problems.push(`Line ${lineNumber}: could not read an amount that is not zero`);
      return;
    }

    lines.push({
      txnDate,
      description,
      amount,
      reference: columns.reference !== undefined ? cells[columns.reference] || null : null,
    });
  });

  return { lines, problems, skipped: problems.length };
}
